import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { Plus, TrendingUp, AlertCircle, Camera, Users, Wallet, BarChart3, Receipt } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const CATEGORIES = ['food', 'transport', 'accommodation', 'activities', 'shopping', 'other'];
const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6b7280'];

interface Trip {
  id: string;
  title: string;
  budget_by_category: any;
  spent_by_category: any;
  currency: string;
}

interface Expense {
  id: string;
  amount: number;
  category: string;
  description: string;
  expense_date: string;
  receipt_photo_url?: string;
}

export function BudgetTrackingDashboard() {
  const { user } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  
  // Expense form state
  const [expenseForm, setExpenseForm] = useState({
    amount: '',
    category: 'food',
    description: '',
  });

  useEffect(() => {
    if (user) {
      loadTrips();
    }
  }, [user]);

  useEffect(() => {
    if (selectedTrip) {
      loadExpenses();
    }
  }, [selectedTrip]);

  const loadTrips = async () => {
    if (!user) return;

    const { data } = await supabase
      .from('trips')
      .select('id, title, budget_by_category, spent_by_category, currency')
      .eq('user_id', user.id)
      .eq('is_active', true);

    if (data) {
      setTrips(data);
      if (data.length > 0 && !selectedTrip) {
        setSelectedTrip(data[0].id);
      }
    }
  };

  const loadExpenses = async () => {
    if (!selectedTrip) return;

    const { data } = await supabase
      .from('expenses')
      .select('*')
      .eq('trip_id', selectedTrip)
      .order('expense_date', { ascending: false });

    if (data) setExpenses(data);
  };

  const selectedTripData = trips.find(t => t.id === selectedTrip);

  const addExpense = async () => {
    if (!selectedTrip || !user || !expenseForm.amount) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const { error } = await supabase.from('expenses').insert([{
        trip_id: selectedTrip,
        user_id: user.id,
        amount: parseFloat(expenseForm.amount),
        category: expenseForm.category as any,
        description: expenseForm.description,
        currency: selectedTripData?.currency || 'USD',
        expense_date: new Date().toISOString().split('T')[0],
      }]);

      if (error) throw error;

      // Update spent_by_category
      if (selectedTripData) {
        const updatedSpent = { ...selectedTripData.spent_by_category };
        updatedSpent[expenseForm.category] = (updatedSpent[expenseForm.category] || 0) + parseFloat(expenseForm.amount);

        await supabase
          .from('trips')
          .update({ spent_by_category: updatedSpent })
          .eq('id', selectedTrip);
      }

      toast.success('Expense added');
      setExpenseForm({ amount: '', category: 'food', description: '' });
      setShowExpenseDialog(false);
      loadTrips();
      loadExpenses();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    }
  };

  const updateBudget = async (category: string, amount: number) => {
    if (!selectedTrip || !selectedTripData) return;

    const updatedBudget = { ...selectedTripData.budget_by_category };
    updatedBudget[category] = amount;

    const { error } = await supabase
      .from('trips')
      .update({ budget_by_category: updatedBudget })
      .eq('id', selectedTrip);

    if (error) {
      toast.error('Failed to update budget');
    } else {
      toast.success('Budget updated');
      loadTrips();
    }
  };

  const getBudgetStatus = (category: string) => {
    if (!selectedTripData) return { percentage: 0, status: 'safe' };
    
    const budget = selectedTripData.budget_by_category[category] || 0;
    const spent = selectedTripData.spent_by_category[category] || 0;
    
    if (budget === 0) return { percentage: 0, status: 'safe' };
    
    const percentage = (spent / budget) * 100;
    
    let status = 'safe';
    if (percentage >= 100) status = 'over';
    else if (percentage >= 80) status = 'warning';
    
    return { percentage: Math.min(percentage, 100), status };
  };

  const getChartData = () => {
    if (!selectedTripData) return [];
    
    return CATEGORIES.map(cat => ({
      name: cat.charAt(0).toUpperCase() + cat.slice(1),
      budget: selectedTripData.budget_by_category[cat] || 0,
      spent: selectedTripData.spent_by_category[cat] || 0,
    })).filter(item => item.budget > 0 || item.spent > 0);
  };

  const getPieData = () => {
    if (!selectedTripData) return [];
    
    return CATEGORIES.map((cat, idx) => ({
      name: cat.charAt(0).toUpperCase() + cat.slice(1),
      value: selectedTripData.spent_by_category[cat] || 0,
      color: COLORS[idx],
    })).filter(item => item.value > 0);
  };

  const totalBudget = selectedTripData ? Object.values(selectedTripData.budget_by_category as Record<string, number>).reduce((a, b) => a + b, 0) : 0;
  const totalSpent = selectedTripData ? Object.values(selectedTripData.spent_by_category as Record<string, number>).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-4">
      {/* Action bar (always visible) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border bg-muted/30 p-4">
        <div>
          <p className="text-sm text-muted-foreground">Total spent</p>
          <p className="text-2xl font-bold">
            {selectedTripData?.currency} {totalSpent?.toFixed(2)}
            <span className="text-base font-normal text-muted-foreground"> / {totalBudget?.toFixed(2)}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <TrendingUp className="h-4 w-4 mr-2" />
                Set Budget
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Set Category Budgets</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {CATEGORIES.map(cat => (
                  <div key={cat} className="space-y-2">
                    <Label className="capitalize">{cat}</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      defaultValue={selectedTripData?.budget_by_category[cat] || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        updateBudget(cat, value);
                      }}
                    />
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Expense</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat} className="capitalize">
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    placeholder="What did you spend on?"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  />
                </div>
                <Button onClick={addExpense} className="w-full">Add Expense</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Collapsible sections */}
      <Accordion type="single" collapsible defaultValue="overview" className="space-y-3">
        <AccordionItem value="overview" className="border rounded-xl bg-card px-4">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-600 dark:text-cyan-400">
                <Wallet className="h-4 w-4" />
              </span>
              <div className="text-left">
                <p className="font-semibold">Budget Overview</p>
                <p className="text-xs text-muted-foreground font-normal">
                  Progress per category
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-4">
              {CATEGORIES.every(cat => (selectedTripData?.budget_by_category[cat] || 0) === 0) ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No budgets set yet. Click <span className="font-medium">Set Budget</span> to get started.
                </p>
              ) : CATEGORIES.map((cat) => {
                const { percentage, status } = getBudgetStatus(cat);
                const budget = selectedTripData?.budget_by_category[cat] || 0;
                const spent = selectedTripData?.spent_by_category[cat] || 0;
                if (budget === 0) return null;
                return (
                  <div key={cat} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize font-medium">{cat}</span>
                      <span className={status === 'over' ? 'text-destructive' : status === 'warning' ? 'text-orange-500' : ''}>
                        {selectedTripData?.currency} {spent.toFixed(2)} / {budget.toFixed(2)}
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                    {status === 'warning' && (
                      <p className="text-xs text-orange-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        80% of budget used
                      </p>
                    )}
                    {status === 'over' && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Over budget!
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="analytics" className="border rounded-xl bg-card px-4">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-600 dark:text-cyan-400">
                <BarChart3 className="h-4 w-4" />
              </span>
              <div className="text-left">
                <p className="font-semibold">Spending Analytics</p>
                <p className="text-xs text-muted-foreground font-normal">
                  Charts & distribution
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <Tabs defaultValue="bar">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="bar">By Category</TabsTrigger>
                <TabsTrigger value="pie">Distribution</TabsTrigger>
              </TabsList>
              <TabsContent value="bar" className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getChartData()}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="budget" fill="#8b5cf6" name="Budget" />
                    <Bar dataKey="spent" fill="#3b82f6" name="Spent" />
                  </BarChart>
                </ResponsiveContainer>
              </TabsContent>
              <TabsContent value="pie" className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={getPieData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.value.toFixed(0)}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getPieData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </TabsContent>
            </Tabs>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="recent" className="border rounded-xl bg-card px-4">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-600 dark:text-cyan-400">
                <Receipt className="h-4 w-4" />
              </span>
              <div className="text-left">
                <p className="font-semibold">Recent Expenses</p>
                <p className="text-xs text-muted-foreground font-normal">
                  Latest transactions
                </p>
              </div>
              {expenses.length > 0 && (
                <Badge variant="secondary" className="ml-1">{expenses.length}</Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            {expenses.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 text-sm">No expenses recorded yet</p>
            ) : (
              <div className="space-y-3">
                {expenses.slice(0, 10).map(expense => (
                  <div key={expense.id} className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium">{expense.description || 'Unnamed expense'}</p>
                      <p className="text-sm text-muted-foreground capitalize">{expense.category}</p>
                    </div>
                    <p className="font-semibold">
                      {selectedTripData?.currency} {expense.amount.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
